import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import axios from "axios";

// Components
import Navigation from "./components/Navigation";
import Home from "./components/Home";
import EventDetail from "./components/EventDetail";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Register from "./components/Register";
import Calendar from "./components/Calendar";
import LogProgress from "./components/LogProgress";
import Footer from "./components/Footer";
import ManCaveBash from "./components/ManCaveBash";
import CMDashboard from "./components/CMDashboard";
import Series from "./components/Series";
import CrucibleMen from "./components/CrucibleMen";
import MensConference2025 from "./components/MensConference2025";
import Events from "./components/Events";
import SmartDadPlan from "./components/SmartDadPlan";
import AdminDashboard from "./components/AdminDashboard";
import MensConferenceVideos from "./components/MensConferenceVideos";
import DeployConference from "./components/DeployConference";

// -----------------------------------------------------------------------------
// API URL configuration
// -----------------------------------------------------------------------------
const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:4000/api"
    : "https://www.414ministry.org/api";

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common["Content-Type"] = "application/json";

// -----------------------------------------------------------------------------
// Main App Component
// -----------------------------------------------------------------------------
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API_URL}/user`, {
          withCredentials: true,
        });
        setUser(response.data.user);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/analytics/track`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/logout`);
      setUser(null);
      window.location.href = "/login";
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const isLoggedIn = !!user;

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation isLoggedIn={isLoggedIn} user={user} logout={logout} />
        <main className="flex-grow">
          <Routes>
            <Route
              path="/"
              element={<Home isLoggedIn={isLoggedIn} user={user} />}
            />
            <Route path="/event/:eventId" element={<EventDetail />} />
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route
              path="/dashboard"
              element={user ? <Dashboard /> : <Navigate to="/login" />}
            />
            <Route
              path="/calendar"
              element={user ? <Calendar /> : <Navigate to="/login" />}
            />
            <Route
              path="/logprogress"
              element={user ? <LogProgress /> : <Navigate to="/login" />}
            />
            <Route path="/logout" element={<Navigate to="/login" />} />

            {/* Ministry Content */}
            <Route path="/man-cave-bash" element={<ManCaveBash />} />
            <Route path="/cmdashboard" element={<CMDashboard />} />
            <Route path="/cmdashboard/:seriesId" element={<Series />} />
            <Route path="/crucible-men" element={<CrucibleMen />} />
            <Route
              path="/mens-conference-2025"
              element={<MensConference2025 />}
            />
            <Route
              path="/mens-conference-2025/videos"
              element={<MensConferenceVideos />}
            />
            <Route path="/deploy-conference" element={<DeployConference />} />
            <Route path="/events" element={<Events />} />
            <Route path="/smartdadplan" element={<SmartDadPlan />} />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                user?.isAdmin ? (
                  <AdminDashboard user={user} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
